/**
 * CustomerDetails Component
 * Displays customer information from the broker account
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import { services } from "../../../../services/api";
import { Icon } from "../../../../components/ds";
import IciciCustomerDetails from "./BrokerSpecific/Icici/IciciCustomerDetails";
import FlattradeCustomerDetails from "./BrokerSpecific/Flattrade/FlattradeCustomerDetails";

const CustomerDetails = ({ account }) => {
  // Component state
  const [loading, setLoading] = useState(true);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load customer details
  const loadCustomerDetails = useCallback(async () => {
    if (!account?.account_id) {
      setError("No account ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("Fetching customer details for account:", account.account_id);
      const response = await services.brokerAccounts.getCustomerDetails(account.account_id);

      if (response.data && response.data.status === "success") {
        setCustomerDetails(response.data.data);
        setLastUpdated(new Date());
        console.log("Customer details loaded:", response.data.data);
      } else {
        const errorMsg = response.data?.error || response.data?.message || "Failed to load customer details";
        setError(errorMsg);
        console.error("Error loading customer details:", errorMsg);
      }
    } catch (err) {
      console.error("Error in loadCustomerDetails:", err);
      setError(`Failed to load customer details: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [account?.account_id]);

  useEffect(() => {
    if (account?.account_id) {
      loadCustomerDetails();
    }
  }, [account?.account_id, loadCustomerDetails]);

  if (loading) {
    return (
      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
        <div className="p-6 flex items-center justify-center min-h-[120px]">
          <svg className="animate-spin h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <span className="ml-3 text-fg-2">Loading customer details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
        <div className="p-6">
          <Alert 
            severity="error" 
            icon={<Icon name="error" />}
            className="mb-4"
          >
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
          
          <Box className="text-center">
            <Typography variant="body2" className="text-fg-2 mb-3">
              Unable to load customer details. Please check your connection and try again.
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  // Check for broker-specific components
  if (customerDetails) {
    // ICICI broker (broker_id = 1)
    if (account?.broker_id === 1) {
      return (
        <IciciCustomerDetails 
          account={account}
          customerDetails={customerDetails}
          lastUpdated={lastUpdated}
          onRefresh={loadCustomerDetails}
        />
      );
    }

    // Flattrade broker (broker_id = 2)
    if (account?.broker_id === 2) {
      return (
        <FlattradeCustomerDetails 
          account={account}
          customerDetails={customerDetails}
          lastUpdated={lastUpdated}
          onRefresh={loadCustomerDetails}
        />
      );
    }
  }

  // Generic customer details display for other brokers
  return (
    <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Icon name="user" className="text-accent shrink-0" size={28} />
            <div className="min-w-0">
              <Typography variant="h6" className="text-fg-1 font-medium">
                Customer Details
              </Typography>
              <Typography variant="caption" className="text-fg-2">
                Account holder information
              </Typography>
            </div>
          </div>
          {lastUpdated && (
            <Typography variant="caption" className="text-fg-2 shrink-0">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </div>

        <Divider className="border-[var(--divider)] mb-6" />

        {/* Customer Information */}
        {customerDetails ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<Icon name="info" />}>
                Customer details are available. Displaying raw broker data.
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Broker Customer Data
                  </Typography>
                  <pre className="bg-bg-3 text-fg-2 p-3 rounded text-xs overflow-x-auto max-w-full">
                    {JSON.stringify(customerDetails, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <div className="text-center py-8">
            <Typography variant="body1" className="text-fg-2">
              No customer data available
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
